import React, { useState, useCallback, useEffect } from 'react'
import { Button, Box, FormControl, Select, MenuItem, TextField, CircularProgress, Grid } from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import policymanagementApi from '../../../../api/policymanagement.api'
import { useCancelPolicyContext } from './context'
import './style.scss'

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.5rem' }}>
    <div style={{ marginRight: '.5rem' }}>{label}:</div>
    <div>{value}</div>
  </div>
)

export const ConfirmCancelation = () => {
  const [fetchingRefund, setFetchingRefund] = useState(false)
  const [refundData, setRefundData] = useState(null)
  const [state, dispatch] = useCancelPolicyContext()
  const { cancelationDate, cancelationReason, cancelationAction, cancelationText, odometerValues, odometerImages, insuranceId } = state

  const getCanceledPolicyRefund = useCallback(async () => {
    setFetchingRefund(true)
    try {
      const data = {
        insuranceId,
        cancelationDate,
        cancelationReason,
        cancelationAction,
        cancelationText,
        odometerImages,
        odometerValues,
      }
      const res = await policymanagementApi.getCanceledPolicyRefund(data)
      if (res.status !== 200) {
        throw new Error(res.statusText)
      }
      return res.data
    } catch (error) {
      console.log(error)
    } finally {
      setFetchingRefund(false)
    }
  }, [dispatch])

  useEffect(() => {
    ;(async () => {
      const res = await getCanceledPolicyRefund()
      setRefundData(res)
    })()
  }, [])

  if (state.status === 'busy')
    return (
      <div
        style={{
          minHeight: '12rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid wrap="nowrap" justifyContent="center" container>
          <CircularProgress />
        </Grid>
      </div>
    )
  return (
    <div
      style={{
        minHeight: '12rem',
        width: '50%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Row label="Cancelation Date" value={new Date(state.cancelationDate).toLocaleDateString()} />
      <Row label="Cancelation Action" value={state.cancelationAction} />
      <Row label="Cancelation Reason" value={state.cancelationReason} />
      <Row label="Cancelation Text" value={state.cancelationText} />

      {fetchingRefund ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '9.5rem' }}>
          <div className="spinner"></div>
        </div>
      ) : (
        <div>
          <div>Refund information:</div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Fixed</div>
            <div>{refundData && refundData.fixed_price}</div>
          </div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Per miles</div>
            <div>{refundData && refundData.per_mile_price}</div>
          </div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Add On Coverages</div>
            <div>{refundData && refundData.add_on_coverages_price}</div>
          </div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Fees</div>
            <div>{refundData && refundData.fees}</div>
          </div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Minimum Earned Premium:</div>
            <div>{refundData && refundData.minimum_earned_premium}</div>
          </div>
          <div style={{ display: 'flex', marginLeft: '4rem', justifyContent: 'space-between', marginBottom: '.5rem' }}>
            <div>Total</div>
            <div>{refundData && refundData.total_price}</div>
          </div>
        </div>
      )}

      <div style={{ margin: '.5rem 0' }}>Vehicle Milage:</div>
      <div>
        {state.vehicles.map(({ id, make, model }) => (
          <div key={id} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              {make} {model}
            </div>
            <div>{state.odometerValues[id]}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const SelectDate = ({ start, end }) => {
  const [{ cancelationDate, startDate, perMileUntil }, dispatch] = useCancelPolicyContext()
  return (
    <div
      style={{
        minHeight: '12rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <KeyboardDatePicker
        {...(process.env.REACT_APP_ALLOW_CANCEL_DATE !== 'true' && {
          minDate: start || startDate,
          maxDate: end || perMileUntil,
        })}
        fullWidth
        InputLabelProps={{ shrink: true }}
        inputVariant="outlined"
        id="date-picker-dialog"
        label="Select Date"
        clearable
        value={cancelationDate}
        onChange={date =>
          dispatch({
            type: 'SET_CANCELATION_DATE',
            payload: date,
          })
        }
      />
    </div>
  )
}

export const CancelationReason = () => {
  const [{ cancelationReason, cancelationText }, dispatch] = useCancelPolicyContext()
  return (
    <>
      <FormControl variant="outlined" fullWidth style={{ marginBottom: '2rem' }}>
        <Select
          labelId="reason"
          value={cancelationReason}
          onChange={e =>
            dispatch({
              type: 'SET_CANCELATION_REASON',
              payload: e.target.value,
            })
          }
        >
          {CancellationReasons.map(reason => (
            <MenuItem value={reason} key={reason}>
              {reason.replace(/([a-z0-9])([A-Z])/g, '$1 $2')}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" fullWidth style={{ marginBottom: '2rem' }}>
        <TextField
          placeholder="Add free text"
          variant="outlined"
          multiline
          maxRows={4}
          value={cancelationText}
          onChange={e =>
            dispatch({
              type: 'SET_CANCELATION_TEXT',
              payload: e.target.value,
            })
          }
        />
      </FormControl>
    </>
  )
}

export const UploadImage = ({ id }) => {
  const [{ odometerImages }, dispatch] = useCancelPolicyContext()
  return (
    <>
      <input
        accept="image/*"
        type="file"
        id={id}
        multiple
        style={{ display: 'none' }}
        onChange={e =>
          dispatch({
            type: 'ADD_ODOMETER_IMAGE',
            payload: {
              id,
              file: e.target.files[0],
            },
          })
        }
      />
      {id in odometerImages ? (
        <Box key={odometerImages[id].name} textAlign="center">
          <img src={URL.createObjectURL(odometerImages[id])} alt={odometerImages[id].name} height="100px" />
        </Box>
      ) : (
        <label htmlFor={id}>
          <Button variant="contained" color="primary" component="span">
            Upload Image
          </Button>
        </label>
      )}
    </>
  )
}

export const VehicleMilage = ({ uploadImage = false }) => {
  const [{ vehicles, odometerValues }, dispatch] = useCancelPolicyContext()
  return vehicles.length > 0 ? (
    vehicles.map(({ id, make, model }) => (
      <div
        key={id}
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
          }}
        >
          <div>
            {make} {model}
          </div>
          <TextField
            type="number"
            variant="outlined"
            placeholder="enter milage"
            value={id in odometerValues ? odometerValues[id] : ''}
            onChange={e =>
              dispatch({
                type: 'ADD_ODOMETER_MILAGE',
                payload: { id, value: e.target.valueAsNumber },
              })
            }
          />
        </div>
        {uploadImage && <UploadImage id={id} />}
      </div>
    ))
  ) : (
    <div>No Vehicles in policy</div>
  )
}

export const CancellationReasons = [
  'SoldBike',
  'BetterOffer',
  'CCFailure',
  'DefaultMileage',
  'TechnicalCancel',
  'Other',
  'FailedActivation',
  'PolicyRewrite',
  'RoadsideRefund',
  'ChangedHisMind',
  'UnacceptableOperatorPerMVR',
  'PaymentFailure',
  'DidNotSay',
  'Storage',
  'Seasonality',
  'NoValidMotorcycleEndorsement',
]

export const PurchaseCancellationActionType = [
  'BackdatedFlat',
  'FreeOfCharge',
  'ManualOdometerUpload',
  'ManualOdometerUploadNoImage',
  'Regular',
  'NoOdometer',
]

/// utilities
/**
 * @description Gets a tree node from a root node by recursion, traversing the tree
 * @returns TreeNode
 */
export const getTreeNode = nodeName => root => {
  if (nodeName && root.maybeChildren) {
    const steps = nodeName.split('.')
    const targetNode = steps.reduce(
      (node, child) =>
        typeof node === 'function' ? node().maybeChildren && node().maybeChildren[child] : node.maybeChildren && node.maybeChildren[child],
      root
    )
    return typeof targetNode === 'function' ? targetNode(root) : targetNode
  }
  return root
}

/**
 *
 * @description Create a tree node using passed parameters, may include child nodes
 */
export const createTreeNode = node => ({
  display: {
    caption: node.display.caption,
    ...(node.display.options && { maybeOptions: node.display.options }),
    ...(node.display.component && {
      maybeComponent: node.display.component,
    }),
    ...(node.display.nextCaption && { nextCaption: node.display.nextCaption }),
  },
  ...(node.validation && { maybeValidation: node.validation }),
  ...(node.children && { maybeChildren: node.children }),
  ...(node.back && { maybeBack: node.back }),
  ...(node.next && { maybeNext: node.next }),
  ...(node.last && { last: node.last }),
})
